<template>
  <div class="space-center login">
    <h2 class="login-title">修改密码</h2>

    <div class="form">
      <!-- 账号输入 -->
      <div class="input-group" :class="{ 'has-error': touched.account && errors.account }">
        <input
          v-model="formData.account"
          type="text"
          placeholder="请输入账号/手机号"
          class="main-input"
          @blur="markTouched('account'); checkAccount()"
          @input="checkAccount"
        />
        <div class="svg">
          <svg-icon name="用户" color="svg" />
        </div>
        <div v-if="touched.account && errors.account" class="error-message">
          {{ errors.account }}
        </div>
      </div>

      <!-- 原密码输入 -->
      <div class="input-group" :class="{ 'has-error': touched.oldPassword && errors.oldPassword }">
        <input
          v-model="formData.oldPassword"
          :type="showOldPassword ? 'text' : 'password'"
          placeholder="请输入原密码"
          class="main-input"
          @blur="markTouched('oldPassword'); checkOldPassword()"
          @input="checkOldPassword"
        />
        <div class="svg password-icon" @click="toggleOldPassword">
          <svg-icon :name="showOldPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="touched.oldPassword && errors.oldPassword" class="error-message">
          {{ errors.oldPassword }}
        </div>
      </div>

      <!-- 新密码输入 -->
      <div class="input-group" :class="{ 'has-error': touched.newPassword && errors.newPassword }">
        <input
          v-model="formData.newPassword"
          :type="showNewPassword ? 'text' : 'password'"
          placeholder="请输入新密码（6-20位）"
          class="main-input"
          @blur="markTouched('newPassword'); checkNewPassword()"
          @input="checkNewPassword"
        />
        <div class="svg password-icon" @click="toggleNewPassword">
          <svg-icon :name="showNewPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="touched.newPassword && errors.newPassword" class="error-message">
          {{ errors.newPassword }}
        </div>
      </div>

      <!-- 确认新密码输入 -->
      <div class="input-group" :class="{ 'has-error': touched.confirmPassword && errors.confirmPassword }">
        <input
          v-model="formData.confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          placeholder="请再次输入新密码"
          class="main-input"
          @blur="markTouched('confirmPassword'); checkConfirmPassword()"
          @input="checkConfirmPassword"
        />
        <div class="svg password-icon" @click="toggleConfirmPassword">
          <svg-icon :name="showConfirmPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="touched.confirmPassword && errors.confirmPassword" class="error-message">
          {{ errors.confirmPassword }}
        </div>
      </div>

      <!-- 确认修改按钮 -->
      <button class="Signup-Btn submit-btn" :disabled="loading" @click="handleSubmit">
        <svg-icon v-if="loading" class="icon-loading" size="18" name="加载" color="#fff" />
        <span class="button-text">{{ loading ? '修改中...' : '确认修改' }}</span>
      </button>

    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { toast,encodePass } from 'd3components/src/utils/tools'
// import { resetPassword as resetPasswordApi } from '@/utils/auth'
import {ApiUser} from 'd3components'
const { updateUserPass }  = ApiUser;
defineOptions({ name: 'forgot_password' })

const router = useRouter()

// ==================== 状态管理 ====================
const loading = ref(false)
const showOldPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// 表单数据
const formData = ref({
  account: '',
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

// 字段触摸状态
const touched = ref({
  account: false,
  oldPassword: false,
  newPassword: false,
  confirmPassword: false,
})

// 字段错误信息
const errors = ref({
  account: '',
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})



// ==================== 密码显示切换 ====================
const toggleOldPassword = () => {
  showOldPassword.value = !showOldPassword.value
}

const toggleNewPassword = () => {
  showNewPassword.value = !showNewPassword.value
}

const toggleConfirmPassword = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

// ==================== 字段验证（实时） ====================
const markTouched = (field: keyof typeof touched.value) => {
  touched.value[field] = true
}

const checkAccount = () => {
  if (!touched.value.account) return
  
  if (!formData.value.account) {
    errors.value.account = '请输入账号'
  } else if (formData.value.account.length < 2) {
    errors.value.account = '账号至少2个字符'
  } else {
    errors.value.account = ''
  }
}

const checkOldPassword = () => {
  if (!touched.value.oldPassword) return
  
  if (!formData.value.oldPassword) {
    errors.value.oldPassword = '请输入原密码'
  } else if (formData.value.oldPassword.length < 2) {
    errors.value.oldPassword = '密码至少2位'
  } else if (formData.value.oldPassword.length > 20) {
    errors.value.oldPassword = '密码最多20位'
  } else {
    errors.value.oldPassword = ''
  }
}

const checkNewPassword = () => {
  if (!touched.value.newPassword) return
  
  if (!formData.value.newPassword) {
    errors.value.newPassword = '请输入新密码'
  } else if (formData.value.newPassword.length < 6) {
    errors.value.newPassword = '密码至少6位'
  } else if (formData.value.newPassword.length > 20) {
    errors.value.newPassword = '密码最多20位'
  } else if (formData.value.newPassword === formData.value.oldPassword) {
    errors.value.newPassword = '新密码不能与原密码相同'
  } else {
    errors.value.newPassword = ''
  }
}

const checkConfirmPassword = () => {
  if (!touched.value.confirmPassword) return
  
  if (!formData.value.confirmPassword) {
    errors.value.confirmPassword = '请确认新密码'
  } else if (formData.value.confirmPassword !== formData.value.newPassword) {
    errors.value.confirmPassword = '两次密码不一致'
  } else {
    errors.value.confirmPassword = ''
  }
}

// ==================== 表单提交验证 ====================
const validateAllFields = (): boolean => {
  // 标记所有字段为已触摸
  Object.keys(touched.value).forEach(key => {
    touched.value[key as keyof typeof touched.value] = true
  })
  
  // 触发所有验证
  checkAccount()
  checkOldPassword()
  checkNewPassword()
  checkConfirmPassword()
  
  // 检查是否有错误
  const firstError = 
    errors.value.account ||
    errors.value.oldPassword ||
    errors.value.newPassword ||
    errors.value.confirmPassword
  
  if (firstError) {
    toast(firstError, 3)
    return false
  }
  
  return true
}

// ==================== 提交修改密码 ====================
const handleSubmit = async () => {
  if (!validateAllFields()) return

  loading.value = true
  
  try {
    // 注意：这里使用 code 字段传递原密码，因为 API 可能需要这个字段名
    const res = await updateUserPass({
      oldPwd: encodePass(formData.value.oldPassword),
      newPwd: encodePass(formData.value.newPassword)
    })
    
    console.log(res,'---res')
    if (res && res.code === 0) {
      toast('密码更新成功，请重新登录', 1);
      setTimeout(() => {
        router.replace('/login')
      }, 1000)
    } else {
      toast(res?.value || '修改失败，请检查原密码是否正确', 3)
    }
  } catch (err: any) {
    toast(err.errMessage ? err.errMessage : '更新异常', 3);
  } finally {
    loading.value = false
  }
}

defineExpose({
  hasPrivacyConsent: () => true,
})
</script>

<style scoped lang="scss">
  // ===== 科技感配色 =====
  // $primary-gradient: linear-gradient(135deg, rgba(57, 140, 254, 1) 0%, rgba(26, 111, 220, 1) 100%);
  // $accent-color: rgba(57, 140, 254, 1);
  $primary-gradient: var(--color-primary);
  $accent-color: var(--color-primary);
  $light-accent: rgba(57, 140, 254, 0.1);
  $text-secondary: #8a94a6;
  $border-light: rgba(57, 140, 254, 0.2);

  // ===== 间距变量 =====
  $spacing-xs: 0.25rem; // 4px
  $spacing-sm: 0.5rem; // 8px
  $spacing-md: 0.75rem; // 12px
  $spacing-lg: 1.25rem; // 20px
  $spacing-xl: 1.5rem; // 24px
  $spacing-2xl: 1.75rem; // 28px
  $spacing-3xl: 2rem; // 32px

  // ===== 圆角变量 =====
  $radius-sm: 2px;
  $radius-md: 6px;
  $radius-lg: 12px;
  $radius-xl: 2rem;
  $radius-full: 44px;
  $radius-input: 54px;

  // ===== 阴影变量 =====
  $shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.02);
  $shadow-md: 0 4px 16px rgba($accent-color, 0.3);
  $shadow-lg: 0 6px 20px rgba($accent-color, 0.45);
  $shadow-focus:
    0 0 0 4px rgba($accent-color, 0.1),
    0 6px 20px rgba($accent-color, 0.12);
  $shadow-hover: 0 4px 16px rgba($accent-color, 0.08);
  $shadow-active: 0 2px 10px rgba($accent-color, 0.3);

  // ===== 字体变量 =====
  $font-size-sm: 0.72rem;
  $font-size-base: 0.95rem;
  $font-size-lg: 1rem;
  $font-weight-regular: 500;
  $font-weight-bold: 600;
  $font-weight-black: 900;

  // ===== 动画变量 =====
  $transition-fast: 0.3s ease;
  $transition-base: 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  $transition-slow: 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  // ===== 混合宏 - 渐变文本效果 =====
  @mixin gradient-text {
    background: $primary-gradient;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.1em;
  }

  // ===== 混合宏 - 现代输入框组样式 =====
  @mixin input-group-style {
    position: relative;
    margin-top: $spacing-xl;
    background: #fafbfc;
    border: 2px solid #e8ecf2 !important;
    border-radius: $radius-input;
    padding: 20px 54px;
    transition: all $transition-base;
    box-shadow: $shadow-sm;

    // 悬浮效果
    &:hover {
      border-color: rgba($accent-color, 0.4);
      background: #fff;
      box-shadow: $shadow-hover;
    }

    // 聚焦效果
    &:focus-within {
      border-color: $accent-color !important;
      background: #fff;
      box-shadow: $shadow-focus;

      .lebal-tilte {
        color: $accent-color !important;
        font-weight: $font-weight-bold;
      }

      .svg {
        opacity: 1;
        color: $accent-color;
      }
    }
  }

  // ===== 混合宏 - 主输入框样式 =====
  @mixin main-input-style {
    border: none !important;
    border-bottom: none !important;
    background: transparent !important;
    padding: 0.5rem 0.5rem 0.5rem 0.25rem;
    font-size: $font-size-lg;
    font-weight: $font-weight-regular;
    color: #2d3748;
    width: 100%;
    transition: all $transition-fast;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: $text-secondary;
    }
  }

  // ===== 混合宏 - 渐变按钮样式 =====
  @mixin gradient-button {
    background: $primary-gradient;
    border: none;
    color: #fff;
    font-weight: $font-weight-bold;
    letter-spacing: 0.15em;
    box-shadow: $shadow-md;
    transition: all $transition-slow;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s ease;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: $shadow-lg;

      &::before {
        left: 100%;
      }
    }

    &:active {
      transform: translateY(0) scale(0.98);
      box-shadow: $shadow-active;
    }
  }

  // ===== 混合宏 - 验证码按钮样式 =====
  @mixin verify-button {
    position: absolute;
    right: 6px;
    top: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translateY(-50%);
    background: $primary-gradient;
    color: #fff;
    border: none;
    height: 80% !important;
    border-radius: $radius-xl;
    font-size: 0.8rem;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: all $transition-fast;
    box-shadow: 0 2px 6px rgba($accent-color, 0.25);
    white-space: nowrap;
    width: 30%;
    font-weight: 700;

    &:hover:not(.disabled) {
      box-shadow: 0 3px 10px rgba($accent-color, 0.35);
    }

    &:active:not(.disabled) {
      transform: translateY(-50%) scale(0.96);
    }

    // 禁用状态
    &.disabled {
      background: #ccc;
      cursor: not-allowed;
      box-shadow: none;
    }
  }

  .login {
    width: 100%;

    // ===== 标题动画样式 =====
    h2 {
      @include gradient-text;
      position: relative;
    }

    // ===== 表单容器增强 =====
    :deep(.form) {
      padding: $spacing-xl 0;
      width: 80%;
      // 输入框组 - 现代卡片式设计
      .input-group {
        @include input-group-style;

        // 错误状态样式
        &.has-error {
          border-color: #f56c6c !important;
          background: #fef0f0;

          &:hover {
            border-color: #f56c6c !important;
            background: #fef0f0;
          }

          &:focus-within {
            border-color: #f56c6c !important;
            background: #fef0f0;
            box-shadow: 0 0 0 4px rgba(245, 108, 108, 0.1);

            .svg {
              color: #f56c6c;
            }
          }

          .svg {
            color: #f56c6c;
          }
        }

        // 错误提示文字（绝对定位，不占据高度）
        .error-message {
          position: absolute;
          left: 54px;
          right: 54px;
          top: 100%;
          margin-top: 8px;
          font-size: 24px;
          color: #f56c6c;
          line-height: 1.2;
          font-weight: 500;
          animation: slideDown 0.3s ease;
          z-index: 10;
        }

        // 输入框本身
        .main-input {
          @include main-input-style;
        }

        // 浮动标签
        .lebal-tilte {
          position: absolute;
          left: 0.85rem;
          top: 50%;
          transform: translateY(-50%);
          font-size: $font-size-base;
          color: #9ca3af;
          font-weight: $font-weight-regular;
          pointer-events: none;
          transition: all $transition-base;
          background: transparent;
          padding: 0 $spacing-xs;
        }

        // 标签上浮状态
        .main-input:focus ~ .lebal-tilte,
        .main-input:valid ~ .lebal-tilte {
          top: 0.35rem;
          transform: translateY(0);
          font-size: $font-size-sm;
          color: $accent-color;
          letter-spacing: 0.03em;
        }

        // 右侧图标
        .svg {
          position: absolute;
          right: 0.9rem;
          top: 45%;
          transform: translateY(-50%);
          font-size: 1.2rem;
          color: $accent-color;
          opacity: 0.7;
          transition: all $transition-base;

          &.password-icon {
            cursor: pointer;
            &:hover {
              opacity: 1;
              transform: translateY(-50%) scale(1.1);
            }
          }
        }

        // 隐藏原有的highlight-span
        .highlight-span {
          display: none;
        }

        // 验证码按钮样式
        .Verify-Btn {
          @include verify-button;
        }
      }

      // 第二个输入框间距调整
      .input-group + .input-group,
      .input-group[style*='margin-top'] {
        margin-top: $spacing-lg !important;
      }

      // 重置密码按钮增强
      .Signup-Btn {
        @include gradient-button;
        margin-top: $spacing-2xl !important;
        padding: 0 $spacing-xl;
        height: 88px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 88px;
        font-size: $font-size-lg;
        border-radius: $radius-full;
      }
    }
  }

  // ===== 标题样式 =====
  .login-title {
    font-weight: $font-weight-black;
    margin-bottom: 1.3rem;
  }

  // ===== 验证码输入框组样式 =====
  .verify-code-group {
    display: flex;
    justify-content: space-between;

    .main-input {
      width: 60%;
    }
  }

  // ===== 提交按钮样式 =====
  .submit-btn {
    margin-top: 30px;
  }

  // ===== 按钮文本样式 =====
  .button-text {
    margin-left: 10px;
  }

  // ===== 返回登录链接 =====
  .back-link {
    margin-top: $spacing-lg;
    text-align: center;
    font-size: 28px;
    color: $accent-color;
    font-weight: $font-weight-bold;
    cursor: pointer;
    transition: opacity $transition-fast;
    user-select: none;

    &:hover {
      opacity: 0.8;
    }
  }

  // 错误提示滑入动画
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
